import { useEffect, useState } from "react";
import {
  fetchCustomerMasterByOrgId,
  downloadCustomerDataExcel,
} from "../Api/ViewCustomerMaster";
import { Container, Table, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import { useAuth } from "../Context/AuthContext";
import { useNavigate } from "react-router-dom";
import { getCustomerTypebyOrgId } from "../Api/CustomerMaster";

const ViewCustomerMaster = () => {
  const [customers, setCustomers] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [customerTypes, setCustomerTypes] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  // ✅ Load both Customers and Customer Types
  useEffect(() => {
    const loadData = async () => {
      try {
        if (!user?.organisationId) return;

        const [customerData, typeData] = await Promise.all([
          fetchCustomerMasterByOrgId(user.organisationId),
          getCustomerTypebyOrgId(user.organisationId),
        ]);

        setCustomers(customerData || []);
        setCustomerTypes(typeData || []);
      } catch (error) {
        toast.error(`🦄 Error Fetching Customer Data`);
        console.error(error);
      }
    };

    loadData();
  }, [user]);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await downloadCustomerDataExcel();

      if (!response.data) {
        throw new Error("No data received from server");
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "CustomerMaster_Data.xlsx");

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        link.parentNode.removeChild(link);
      }, 100);

      toast.success("Customer data download started!");
    } catch (error) {
      console.error("Download error:", error);
      toast.error(`🦄 Download failed: ${error.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  // ✅ Helper to map code → description
  const getCustomerTypeDescription = (code) => {
    if (!code) return "";
    const type = customerTypes.find((t) => t.code === code);
    return type ? type.description : code; // fallback to code if not found
  };

  return (
    <>
      <div className="main-content expand">
        <Container fluid className="list-container">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span className="list-title">View Customer Master</span>
            <Button
              className="generate-btn"
              variant="primary"
              onClick={handleDownload}
              disabled={isDownloading || customers.length === 0}
            >
              {isDownloading ? "Downloading..." : "Download Data"}
            </Button>
          </div>

          {customers.length > 0 ? (
            <div className="table-wrapper">
              <div className="table-scroll-container">
                <Table bordered hover className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Customer Code</th>
                      <th>Name</th>
                      <th>Contact Details</th>
                      <th>City</th>
                      <th>Country Code</th>
                      <th>Tax ID</th>
                      <th>Currency</th>
                      <th>Customer Type</th>
                      <th>Active</th>
                      <th>Short Names</th>
                      <th>Entered By</th>
                      <th>Entered Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((customer, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{customer.customerCode}</td>
                        <td>{customer.name}</td>
                        <td>{customer.mobileNumber}</td>
                        <td>{customer.cityName}</td>
                        <td>{customer.countryCode}</td>
                        <td>{customer.gstno ?? customer.taxId}</td>
                        <td>{customer.currencyCode}</td>

                        {/* ✅ Map customerType code to description */}
                        <td>
                          {getCustomerTypeDescription(customer.customerType)}
                        </td>

                        <td>{customer.active}</td>
                        <td>{customer.shortNames}</td>
                        <td>{customer.enteredBy}</td>
                        <td>{moment(customer.createdOnDate).format("L")}</td>

                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() =>
                              navigate(`/editCustomer/${customer.customerCode}`)
                            }
                            title="Edit Customer Details"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              viewBox="0 0 16 16"
                            >
                              <path d="M12.146.854a.5.5 0 0 1 .708 0l2.292 2.292a.5.5 0 0 1 0 .708l-9.193 9.193a.5.5 0 0 1-.168.11l-4 1.5a.5.5 0 0 1-.65-.65l1.5-4a.5.5 0 0 1 .11-.168l9.193-9.193zM11.207 3L13 4.793 12.207 5.586 10.414 3.793 11.207 3zM10.5 3.5L12 5l-7.5 7.5-1 0.4 0.4-1L10.5 3.5z" />
                            </svg>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          ) : (
            <p className="no-data">No customer data found.</p>
          )}
        </Container>
      </div>
    </>
  );
};

export default ViewCustomerMaster;
